import type { FetchResult } from '@apollo/client';

import clientSideApiService from '@/lib/clientSideApiService';
import { SaveSitePageMutationData } from '@/mutations/SaveSitePage';
import { UpdateStylesMutationResult } from '@/mutations/UpdateStyles';
import { DraftSection } from '@/types/DraftSection';
import { GlobalStyles } from '@/types/GlobalStyles';
import { MessageAction, Preset } from '@/utils/constants';

export const determinePreset = (presetValue: unknown): Preset => {
  const isPreset = (value: unknown): value is Preset =>
    Object.values(Preset).includes(value as Preset);

  return isPreset(presetValue) ? presetValue : Preset.Default;
};

export type SavePresetResponse = {
  success: boolean;
};

export const savePreset = async (
  preset: Preset,
  globalStyles: GlobalStyles | null,
  sectionAttributes: DraftSection[],
  sitePageId: string
): Promise<SavePresetResponse> => {
  const resp: SavePresetResponse = { success: false };
  if (!globalStyles) {
    postMessageToParent(MessageAction.PresetSavedFailure, {
      preset,
      errors: ['Failed to save preset. Global styles are missing'],
    });
    return resp;
  }

  try {
    const updatePromises: Promise<
      FetchResult<UpdateStylesMutationResult | SaveSitePageMutationData>
    >[] = [
      clientSideApiService.updateGlobalStyles(
        globalStyles,
        preset.toUpperCase()
      ),
      clientSideApiService.saveSitePage(sitePageId, sectionAttributes),
    ];

    const fetchResults = await Promise.all(updatePromises);

    // Iterate thru all mutation results and look for errors
    // The caveat here is that if one succeeds and the other does not,
    // There is no rollback mechanism as the graph mutations would have already been executed
    // eslint-disable-next-line no-restricted-syntax
    for (const fetchResult of fetchResults) {
      const { data: updateResponse } = fetchResult;

      // If either mutation did not return a response, return a failure reponse
      if (!updateResponse) {
        postMessageToParent(MessageAction.PresetSavedFailure, {
          preset,
          errors: [
            'Failed to save preset. The server did not respond. Please try again.',
          ],
        });
        return resp;
      }

      // If either response contains userErrors, return an failure response
      const userErrors = getSavePresetUserErrors(updateResponse);
      if (userErrors?.length) {
        postMessageToParent(MessageAction.PresetSavedFailure, {
          preset,
          errors: userErrors,
        });
        return resp;
      }
    }
  } catch (err: unknown) {
    const errorMessage =
      err instanceof Error
        ? err.message
        : 'An unexpected error occurred while saving the preset.';
    postMessageToParent(MessageAction.PresetSavedFailure, {
      preset,
      error: errorMessage,
    });
    return resp;
  }

  postMessageToParent(MessageAction.PresetSavedSuccess, { preset });
  resp.success = true;
  return resp;
};

const isUpdateStylesResponse = (
  resp: UpdateStylesMutationResult | SaveSitePageMutationData
): resp is UpdateStylesMutationResult =>
  (resp as UpdateStylesMutationResult).updateStyles !== undefined;

const getSavePresetUserErrors = (
  resp: UpdateStylesMutationResult | SaveSitePageMutationData
) => {
  if (isUpdateStylesResponse(resp)) {
    return resp.updateStyles.userErrors;
  }
  return resp.saveSitePage.userErrors;
};

// This function is used to communicate from Croissant -> Rails
export const postMessageToParent = (type: MessageAction, payload: any) => {
  window.parent.postMessage(
    {
      type,
      payload,
      source: 'croissant',
    },
    window.location.origin
  );
};
